// Credit guard helpers.
//
// NetSuite customer record gives us:
//   terms          string | null   "Net 30", "Net 60", "Due on Receipt", null
//   creditLimit    number          dollar limit (0 / null = no limit set)
//   creditHold     "ON" | "OFF" | "AUTO"
//   openBalance    number          unpaid invoices total
//   unbilledOrders number          unfulfilled SO total not yet invoiced
//
// "Available credit" the SPA cares about for blocking checkout:
//   available = creditLimit - openBalance - unbilledOrders
//
// Customers on terms (terms != null && terms !== "Due on Receipt") cannot
// pay by credit card — paying with a card on a terms account voids those
// terms per Mystery Ranch policy, so we hide the card option entirely
// for those customers and surface a notice.

export function customerOnTerms(customer) {
  const t = (customer?.terms || "").trim().toLowerCase();
  if (!t) return false;
  if (t === "due on receipt" || t === "prepay" || t === "credit card") return false;
  return true;
}

export function availableCredit(customer) {
  if (!customer) return null;
  const limit = Number(customer.creditLimit) || 0;
  if (limit <= 0) return null; // no limit configured
  const open = Number(customer.openBalance) || 0;
  const unbilled = Number(customer.unbilledOrders) || 0;
  return Math.max(0, limit - open - unbilled);
}

// Returns { ok: bool, reason?, available?, limit?, attempted?, overage? }
export function checkCreditForOrder(customer, orderTotal) {
  if (!customer) return { ok: true };
  if (customer.creditHold === "ON") {
    return {
      ok: false,
      reason: "credit_hold",
      message: "Account is on credit hold. Please contact your account manager.",
    };
  }
  const available = availableCredit(customer);
  if (available == null) return { ok: true }; // no limit configured
  const total = Number(orderTotal) || 0;
  if (total > available) {
    return {
      ok: false,
      reason: "credit_limit",
      attempted: total,
      available,
      limit: Number(customer.creditLimit) || 0,
      overage: total - available,
      message:
        `Order total exceeds available credit. ` +
        `Available: $${available.toFixed(2)} of $${(Number(customer.creditLimit) || 0).toFixed(2)} limit.`,
    };
  }
  return { ok: true, available, limit: Number(customer.creditLimit) || 0 };
}

export function allowedPaymentModes(customer) {
  // Card is blocked for terms customers (paying card === losing terms).
  // PO / Net is always allowed if they have terms.
  // Credit hold short-circuits everything.
  if (customer?.creditHold === "ON") return [];
  const onTerms = customerOnTerms(customer);
  const modes = [];
  if (!onTerms) modes.push("card");
  if (onTerms || customer?.creditLimit > 0) modes.push("po");
  if (modes.length === 0) modes.push("po"); // fallback: PO is always available for B2B
  return modes;
}
